import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Coins, ExternalLink, Link as LinkIcon, Mail, MessageCircle, Pencil, Phone, Smartphone } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { instructors as instructorsApi } from '@/api/endpoints'
import { Chips, courseChips, languageChips } from '@/components/ui/ChipGroup'
import { cn, quickRange } from '@/lib/utils'
import { Modal, Spinner } from '@/components/ui'
import EmailList from '@/features/emails/EmailList'
import InstructorForm from '@/features/instructors/InstructorForm'
import { useBootstrap } from '@/hooks/useBootstrap'
import { formatDate, formatMoney, parseIds } from '@/lib/utils'
import { useAuth } from '@/store/auth'
import type { Instructor } from '@/types/api'
import InstructorRates from '@/features/rewards/InstructorRates'
import { rewardTypeLabel } from '@/features/rewards/typeLabel'

type Tab = 'detail' | 'rewards' | 'messenger' | 'portal' | 'emails'

/**
 * Karta inštruktora - záložky namiesto jedného dlhého okna: údaje (s úpravou),
 * odmeny, Messenger, mobilný pohľad a odoslané e-maily. Záložka sa ukáže len
 * tomu, kto jej obsah smie vidieť (Messenger len keď ho škola používa).
 *
 * Odmeny tu sú súhrnom sezóny a poslednými riadkami - celý výplatný list so
 * stránkovaním a filtrom obdobia je `/rewards/:id`, je naň odkaz.
 */
export default function InstructorDetailModal({
  instructorId,
  onClose,
}: {
  instructorId: number
  onClose: () => void
}) {
  const { t } = useTranslation()
  const readOnly = !useAuth((s) => s.user)?.can_write
  const isAdmin = Boolean(useAuth((s) => s.user)?.can_administrate)
  const [editing, setEditing] = useState(false)
  const [tab, setTab] = useState<Tab>('detail')

  const { data: instructor } = useQuery({
    queryKey: ['instructor', instructorId],
    queryFn: () => instructorsApi.get(instructorId),
  })

  const tabs: Tab[] = [
    'detail',
    ...(readOnly ? [] : (['rewards'] as Tab[])),
    ...(instructor?.messenger?.enabled ? (['messenger'] as Tab[]) : []),
    ...(isAdmin ? (['portal', 'emails'] as Tab[]) : []),
  ]

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={instructor?.full_name || t('instructors.title')}
    >
      {!instructor ? (
        <div className="flex h-32 items-center justify-center text-slate-400">
          <Spinner className="h-5 w-5" />
        </div>
      ) : (
        <div className="space-y-3">
          <nav className="-mx-3.5 -mt-3 flex gap-1 overflow-x-auto border-b border-slate-200 px-3.5" role="tablist">
            {tabs.map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={cn(
                  '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium',
                  tab === key
                    ? 'border-brand-600 text-brand-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800',
                )}
              >
                {t(`instructors.tabs.${key}`)}
              </button>
            ))}
          </nav>

          {/* Minimálna výška - okno pri prepínaní záložiek neskáče. */}
          <div className="min-h-[16rem] space-y-3">
          {tab === 'detail' ? (
            <>
              {instructor.deleted || (!readOnly && !editing) ? (
                <div className="flex flex-wrap items-center gap-2">
                  {instructor.deleted ? (
                    <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-700">
                      {t('instructors.deleted')}
                    </span>
                  ) : null}
                  {!readOnly && !editing ? (
                    <button type="button" className="btn-secondary ml-auto" onClick={() => setEditing(true)}>
                      <Pencil size={14} />
                      {t('global.edit')}
                    </button>
                  ) : null}
                </div>
              ) : null}
              {editing ? (
                <InstructorForm instructor={instructor} onDone={() => setEditing(false)} />
              ) : (
                <Details instructor={instructor} />
              )}
            </>
          ) : null}

          {tab === 'rewards' ? <RewardsTab instructorId={instructor.id} /> : null}
          {tab === 'messenger' ? <MessengerBlock instructor={instructor} readOnly={readOnly} /> : null}
          {tab === 'portal' ? <PortalBlock instructor={instructor} readOnly={readOnly} /> : null}

          {/* Inštruktor je príjemcom notifikácií rovnako ako klient - „vieš
              o tom, že ti presunuli zajtrajší blok?“ sa dá overiť tu.
              Telá správ vidí len administrátor. */}
          {tab === 'emails' ? (
            <div className="overflow-hidden rounded-md border border-slate-200">
              <EmailList filter={{ instructor_id: instructorId }} pageSize={10} compact />
            </div>
          ) : null}
          </div>
        </div>
      )}
    </Modal>
  )
}

/** Odmeny v karte - súhrn sezóny a jej prvé riadky, celý výplatný list je
 *  na vlastnej obrazovke. */
function RewardsTab({ instructorId }: { instructorId: number }) {
  const { t } = useTranslation()
  const [season] = useState(() => quickRange('season'))
  const { data, isLoading } = useQuery({
    queryKey: ['instructor-rewards', instructorId, 'card', season.from, season.to],
    queryFn: () => instructorsApi.rewards(instructorId, season.from, season.to, 1, 10),
  })

  if (isLoading || !data) {
    return (
      <div className="flex h-24 items-center justify-center text-slate-400">
        <Spinner className="h-5 w-5" />
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <InstructorRates instructorId={instructorId} />
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-md border border-slate-200 bg-slate-50/70 px-3 py-2.5 text-sm">
        <div>
          <div className="text-xs uppercase text-slate-500">{t('instructors.season')}</div>
          <div className="text-slate-700">
            {formatDate(data.from)} – {formatDate(data.to)}
          </div>
        </div>
        <div className="ml-auto flex gap-5 text-right">
          <div>
            <div className="text-xs uppercase text-slate-500">{t('global.total')}</div>
            <div className="font-semibold">{formatMoney(data.summary.total)}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500">{t('rewards.paid')}</div>
            <div className="font-semibold text-green-700">{formatMoney(data.summary.paid)}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500">{t('rewards.unpaid')}</div>
            <div className="font-semibold text-brand-600">{formatMoney(data.summary.unpaid)}</div>
          </div>
        </div>
      </div>

      {data.items.length ? (
        <table className="table-base [&_tbody_td]:py-1">
          <thead>
            <tr>
              <th className="w-24">{t('reservation.date')}</th>
              <th>{t('rewards.type')}</th>
              <th>{t('reservation.client')}</th>
              <th className="w-28 text-right">{t('rewards.amount')}</th>
              <th className="w-24" />
            </tr>
          </thead>
          <tbody>
            {data.items.map((reward) => (
              <tr key={reward.id}>
                <td className="whitespace-nowrap text-slate-500">{formatDate(reward.reservation_start)}</td>
                <td>
                  {reward.type_id === 999 && reward.description
                    ? reward.description
                    : rewardTypeLabel(t, reward)}
                </td>
                <td className="text-slate-500">{reward.client_name || '—'}</td>
                <td className="text-right tabular-nums">{formatMoney(reward.reward)}</td>
                <td>
                  <span
                    className={cn(
                      'rounded px-1.5 py-0.5 text-[11px] font-medium',
                      reward.paid ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500',
                    )}
                  >
                    {reward.paid ? t('rewards.isPaid') : t('rewards.isUnpaid')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-sm text-slate-500">{t('instructors.noRewardsInSeason')}</p>
      )}

      <div className="flex items-center justify-between text-sm">
        <span className="text-xs text-slate-500">
          {data.count > data.items.length ? t('instructors.rewardsShown', { shown: data.items.length, count: data.count }) : null}
        </span>
        <Link to={`/rewards/${instructorId}`} className="inline-flex items-center gap-1 text-brand-600 hover:underline">
          <Coins size={14} />
          {t('instructors.openPayout')}
        </Link>
      </div>
    </div>
  )
}

function Details({ instructor }: { instructor: Instructor }) {
  const { t } = useTranslation()
  const { data: codebooks } = useBootstrap()


  return (
    <>
      <dl className="grid gap-x-4 gap-y-2 rounded-md border border-slate-200 p-3 text-sm sm:grid-cols-3">
        <Detail label={t('reservation.phone')} value={instructor.tel} icon={<Phone size={12} />} />
        <Detail label={t('reservation.email')} value={instructor.email} icon={<Mail size={12} />} />
        <ChipsDetail label={t('instructors.languages')}>
          <Chips options={languageChips(codebooks?.languages ?? [])} value={parseIds(instructor.idslang)} />
        </ChipsDetail>
        <ChipsDetail label={t('instructors.courses')}>
          <Chips options={courseChips(codebooks?.courses ?? [])} value={parseIds(instructor.idscourses)} />
        </ChipsDetail>
        <Detail label={t('instructors.priority')} value={String(instructor.priority)} />
        <Detail
          label={t('clients.reservationsCount')}
          value={String(instructor.reservations_count ?? 0)}
        />
        <Detail
          label={t('clients.lastReservation')}
          value={formatDate(instructor.last_reservation ?? null)}
        />
        <Detail label={t('rewards.title')} value={formatMoney(instructor.rewards_total)} />
        <Detail label={t('clients.created')} value={formatDate(instructor.created)} />
      </dl>

      {instructor.note ? (
        <div className="rounded-md bg-slate-50 p-3 text-sm">
          <div className="mb-1 text-xs font-medium uppercase text-slate-500">
            {t('global.note')}
          </div>
          <p className="whitespace-pre-line">{instructor.note}</p>
        </div>
      ) : null}
    </>
  )
}

/**
 * Prepojenie s Messengerom.
 *
 * Odkaz je hlavná cesta: inštruktor ho otvorí a bot dostane token sám, takže
 * nepíše nič. Kód pod ním je záloha pre klienta, kde sa odkaz otvoriť nedá —
 * presne ako v e-maile s večerným rozpisom.
 */
function MessengerBlock({
  instructor,
  readOnly,
}: {
  instructor: Instructor
  readOnly: boolean
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [copied, setCopied] = useState<string | null>(null)
  const link = instructor.messenger

  const revoke = useMutation({
    mutationFn: () => instructorsApi.revokeMessenger(instructor.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['instructor', instructor.id] })
      void qc.invalidateQueries({ queryKey: ['instructors'] })
    },
  })

  function copy(value: string) {
    void navigator.clipboard.writeText(value)
    setCopied(value)
    window.setTimeout(() => setCopied(null), 1500)
  }

  if (!link) return null

  return (
    <div className="rounded-md border border-slate-200 p-3 text-sm">
      <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase text-slate-500">
        <MessageCircle size={13} />
        {t('instructors.messenger')}
        {link.linked ? (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] normal-case text-emerald-800">
            {t('instructors.messengerLinked')}
          </span>
        ) : null}
      </div>

      <p className="mb-2 text-xs text-slate-500">
        {link.linked ? t('instructors.messengerLinkedHint') : t('instructors.messengerHint')}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {link.url ? (
          <button type="button" className="btn-secondary" onClick={() => copy(link.url!)}>
            {copied === link.url ? <Check size={14} /> : <LinkIcon size={14} />}
            {t('instructors.messengerCopyLink')}
          </button>
        ) : null}

        {link.token ? (
          <button
            type="button"
            className="font-mono text-[13px] font-bold text-brand-600 hover:underline"
            title={t('instructors.messengerCodeCopy')}
            onClick={() => copy(link.token!)}
          >
            {link.token}
            {copied === link.token ? (
              <Check size={12} className="ml-1 inline text-green-600" />
            ) : null}
          </button>
        ) : null}

        {!readOnly && link.linked ? (
          <button
            type="button"
            className="btn-secondary ml-auto"
            disabled={revoke.isPending}
            onClick={() => revoke.mutate()}
          >
            {revoke.isPending ? <Spinner /> : null}
            {t('instructors.messengerRevoke')}
          </button>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Tajný odkaz na mobilný pohľad inštruktora („Moje lekcie“, `/i/:token`).
 * Inštruktor ho dostáva aj v dennom prehľade e-mailom; nový odkaz starý
 * zneplatní - keď ho niekto prepošle alebo inštruktor odíde.
 */
function PortalBlock({ instructor, readOnly }: { instructor: Instructor; readOnly: boolean }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [copied, setCopied] = useState(false)
  const { data } = useQuery({
    queryKey: ['instructor-portal-link', instructor.id],
    queryFn: () => instructorsApi.portal(instructor.id),
  })
  const renew = useMutation({
    mutationFn: () => instructorsApi.portal(instructor.id, true),
    onSuccess: (result) => qc.setQueryData(['instructor-portal-link', instructor.id], result),
  })
  const url = data ? `${window.location.origin}${data.path}` : ''

  return (
    <div className="rounded-md border border-slate-200 p-3 text-sm">
      <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase text-slate-500">
        <Smartphone size={13} />
        {t('instructors.portal')}
      </div>
      <p className="mb-2 text-xs text-slate-500">{t('instructors.portalHint')}</p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-secondary"
          disabled={!url}
          onClick={() => {
            void navigator.clipboard.writeText(url)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1500)
          }}
        >
          {copied ? <Check size={14} /> : <LinkIcon size={14} />}
          {t('instructors.portalCopy')}
        </button>
        {url ? (
          <a href={url} target="_blank" rel="noreferrer" className="btn-secondary">
            <ExternalLink size={14} />
            {t('instructors.portalOpen')}
          </a>
        ) : null}
        {!readOnly ? (
          <button
            type="button"
            className="btn-secondary ml-auto"
            disabled={renew.isPending}
            onClick={() => {
              if (window.confirm(t('instructors.portalRenewConfirm'))) renew.mutate()
            }}
          >
            {renew.isPending ? <Spinner /> : null}
            {t('instructors.portalRenew')}
          </button>
        ) : null}
      </div>
    </div>
  )
}

function Detail({
  label,
  value,
  icon,
  mono,
}: {
  label: string
  value: string | null | undefined
  icon?: React.ReactNode
  mono?: boolean
}) {
  return (
    <div>
      <dt className="flex items-center gap-1 text-xs uppercase tracking-wide text-slate-500">
        {icon}
        {label}
      </dt>
      <dd className={mono ? 'font-mono text-[13px] font-bold' : 'font-medium'}>{value || '—'}</dd>
    </div>
  )
}

function ChipsDetail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase text-slate-500">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  )
}
